import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Crown, AlertOctagon, Clock, ShieldAlert, BarChart3, X, Download } from "lucide-react";
import DateFilter from "../components/layout/DateFilter";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, LabelList, Cell,
} from "recharts";

const DEPT_COLORS = {
  MOLD:       "#00D4FF",
  FRAME:      "#FBBF24",
  GFRC:       "#A78BFA",
  FINISH:     "#EC4899",
  FIBERGLASS: "#34D399",
};

const ALL_DEPTS = ["MOLD", "FRAME", "GFRC", "FINISH", "FIBERGLASS"];

// Categorical palette for the Pareto bars (one color per Item Desc so each
// defect category is visually distinct instead of everything being amber).
const BAR_PALETTE = [
  "#00D4FF", "#FBBF24", "#EC4899", "#34D399", "#A78BFA",
  "#F87171", "#60A5FA", "#FB923C", "#4ADE80", "#F472B6",
  "#22D3EE", "#FACC15", "#818CF8", "#2DD4BF", "#FDA4AF",
];
const colorForItem = (item) => {
  const key = String(item || "").trim() || "—";
  // Stable hash → palette index.
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return BAR_PALETTE[h % BAR_PALETTE.length];
};

const fmtH = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}h`);

export default function GmView() {
  const { user } = useAuth();
  const isAdmin = user?.role_name === "Administrator" || user?.role === "admin";
  const plantScope = String(user?.plant_scope || "BOTH").toUpperCase();
  const canSeeSLC = isAdmin || plantScope === "SLC" || plantScope === "BOTH";
  const canSeeMex = isAdmin || plantScope === "MEX" || plantScope === "BOTH";

  const [filter, setFilter] = useState({ preset: "all" });
  const [showMexicoState, setShowMexico] = useState(() => !canSeeSLC && canSeeMex);
  const showMexico = !canSeeMex ? false : (!canSeeSLC ? true : showMexicoState);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [modifiedBy, setModifiedBy] = useState("");
  const [metric, setMetric] = useState("count");
  const [data, setData] = useState({ groups: [], per_dept: [], options: { all_depts: ALL_DEPTS, jobs: [], created_by: [], modified_by: [] } });
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [drillRows, setDrillRows] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    if (selectedDepts.length > 0)   p.depts = selectedDepts.join(",");
    if (selectedJob)                p.job = selectedJob;
    if (createdBy)                  p.created_by = createdBy;
    if (modifiedBy)                 p.modified_by = modifiedBy;
    return p;
  }, [filter, showMexico, selectedDepts, selectedJob, createdBy, modifiedBy]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/gm-view/ncr-report", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  useEffect(() => {
    if (!drill) { setDrillRows(null); return; }
    let cancelled = false;
    setDrillLoading(true);
    api.get("/gm-view/ncr-report/rows", {
      params: { ...params, problem: drill.problem, item: drill.item },
    })
      .then((r) => !cancelled && setDrillRows(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setDrillLoading(false));
    return () => { cancelled = true; };
  }, [drill, params]);

  // ESC closes drawer
  useEffect(() => {
    if (!drill) return;
    const onKey = (e) => { if (e.key === "Escape") setDrill(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill]);

  const toggleDept = (d) => {
    setSelectedDepts((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  };

  // Flatten groups → per-problem rows (each row keeps its parent item name for tooltip)
  const problems = useMemo(() => {
    const out = [];
    (data.groups || []).forEach((g) => {
      const perProblem = g.problems.reduce((s, p) => s + p.count, 0) > 0
        ? g.total_hours / g.problems.reduce((s, p) => s + p.count, 0) : 0;
      g.problems.forEach((p) => {
        out.push({
          label: p.label, item: g.item, count: p.count,
          hours: +(perProblem * p.count).toFixed(1),
        });
      });
    });
    return out.sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  }, [data.groups, metric]);

  const maxVal = problems.reduce((m, p) => Math.max(m, p[metric] || 0), 0);

  const activeDepts = (selectedDepts.length ? selectedDepts : (data.options?.all_depts || ALL_DEPTS));

  const clearFilters = () => {
    setSelectedDepts([]); setSelectedJob(""); setCreatedBy(""); setModifiedBy("");
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const downloadPdf = async () => {
    try {
      setPdfLoading(true);
      const r = await api.get("/gm-view/ncr-report/export", {
        params: { ...params, metric },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `gm_view_ncr_${showMexico ? "mexico" : "slc"}_${stamp}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="gm-view-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase flex items-center gap-2" style={{ color: "#FBBF24" }}>
            <Crown size={14} /> 04 · GM View
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">General Manager Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cross-department NCR roll-up · {showMexico ? "MEXICO" : "SLC"} plant scope
          </p>
        </div>
        {canSeeSLC && canSeeMex ? (
          <button type="button" data-testid="gm-toggle-mexico"
            onClick={() => setShowMexico((v) => !v)}
            className={`px-3 py-2 text-[10px] tracking-widest uppercase font-mono border transition-colors ${
              showMexico ? "bg-[#FBBF24] text-[#0A0B10] border-[#FBBF24]" : "bg-[#12141D] text-muted-foreground border-border hover:text-white"
            }`}>
            {showMexico ? "Showing Mexico" : "Show Mexico Plants"}
          </button>
        ) : (
          <span className="px-3 py-2 text-[10px] tracking-widest uppercase font-mono border border-border bg-[#0A0B10] text-muted-foreground">
            Plant: {canSeeMex ? "Mexico" : "SLC"} only
          </span>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard testid="gm-kpi-total"    icon={<AlertOctagon size={16} />} label="Total NCRs" value={loading ? "—" : String(data.total_ncrs ?? 0)} accent="#EC4899" />
        <KpiCard testid="gm-kpi-hours"    icon={<Clock size={16} />}       label="Field Hours" value={loading ? "—" : fmtH(data.total_hours)} accent="#00D4FF" />
        <KpiCard testid="gm-kpi-open"     icon={<AlertOctagon size={16} />} label="Open"        value={loading ? "—" : String(data.open_count ?? 0)} accent="#FBBF24" />
        <KpiCard testid="gm-kpi-breached" icon={<ShieldAlert size={16} />}  label="Breached"    value={loading ? "—" : String(data.breached_count ?? 0)} accent="#F87171" />
      </div>

      {/* Filters card — mirrors NCR Report */}
      <section className="relative bg-[#12141D] border border-border overflow-hidden p-5" data-testid="gm-filters-card">
        <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg,#FBBF24,#FBBF2466)" }} />

        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <AlertOctagon size={18} style={{ color: "#FBBF24" }} />
            <h3 className="font-heading font-bold text-xl">NCR Report — GM View</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button" data-testid="gm-download-pdf"
              onClick={downloadPdf} disabled={pdfLoading || loading}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-mono px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#FBBF24", color: "#0A0B10", borderColor: "#FBBF24" }}
              title="Download the current filtered view as a PDF">
              <Download size={12} /> {pdfLoading ? "Preparing…" : "Download PDF"}
            </button>
            <button
              type="button" data-testid="gm-clear-filters" onClick={clearFilters}
              className="text-[10px] tracking-widest uppercase font-mono px-2 py-1 border border-border text-muted-foreground hover:text-white transition-colors">
              Clear filters
            </button>
          </div>
        </div>

        {/* Filter row */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <DateFilter value={filter} onChange={setFilter} />

          {/* Dept chips (multi-select) */}
          <div className="inline-flex items-center gap-2" data-testid="gm-dept-filter">
            <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono">Dept</span>
            <div className="flex flex-wrap gap-1">
              {ALL_DEPTS.map((d) => {
                const active = selectedDepts.includes(d);
                return (
                  <button key={d} type="button"
                    data-testid={`gm-dept-chip-${d.toLowerCase()}`}
                    onClick={() => toggleDept(d)}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono border transition-colors ${
                      active ? "text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground border-border hover:text-white"
                    }`}
                    style={active ? { backgroundColor: DEPT_COLORS[d], borderColor: DEPT_COLORS[d] } : {}}>
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Job dropdown */}
          <FilterSelect testid="gm-job-filter" label="Job" value={selectedJob} onChange={setSelectedJob}
            options={data.options?.jobs || []} allLabel="All Jobs" />

          {/* Created By */}
          <FilterSelect testid="gm-created-by-filter" label="Created By" value={createdBy} onChange={setCreatedBy}
            options={data.options?.created_by || []} allLabel="All Creators" />

          {/* Modified By */}
          <FilterSelect testid="gm-modified-by-filter" label="Modified By" value={modifiedBy} onChange={setModifiedBy}
            options={data.options?.modified_by || []} allLabel="All Modifiers" />

          <div className="inline-flex border border-border ml-auto" data-testid="gm-metric-toggle">
            <button data-testid="gm-metric-count" onClick={() => setMetric("count")}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
                metric === "count" ? "bg-[#FBBF24] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
              }`}>
              <BarChart3 size={11} /> By Count
            </button>
            <button data-testid="gm-metric-hours" onClick={() => setMetric("hours")}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest flex items-center gap-1.5 transition-colors ${
                metric === "hours" ? "bg-[#00D4FF] text-[#0A0B10]" : "bg-[#0A0B10] text-muted-foreground hover:text-white"
              }`}>
              <Clock size={11} /> By Hours
            </button>
          </div>
        </div>

        {/* Main Pareto chart */}
        {loading && (
          <div className="py-16 text-center text-sm uppercase tracking-widest text-muted-foreground">Loading…</div>
        )}
        {!loading && problems.length === 0 && (
          <div className="py-16 text-center text-sm uppercase tracking-widest text-muted-foreground" data-testid="gm-empty">
            No NCRs match current filters
          </div>
        )}
        {!loading && problems.length > 0 && (
          <div data-testid="gm-chart" style={{ height: `${Math.max(220, problems.length * 30 + 24)}px` }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={problems} layout="vertical" margin={{ top: 5, right: 80, left: 16, bottom: 5 }}>
                <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 10 }}
                  domain={[0, Math.ceil((maxVal || 1) * 1.15)]} />
                <YAxis type="category" dataKey="label" stroke="#94A3B8" tick={{ fontSize: 10 }} width={220} interval={0} />
                <Tooltip cursor={{ fill: "rgba(251,191,36,0.08)" }}
                  contentStyle={{ backgroundColor: "#0A0B10", border: "1px solid #1F2937", fontSize: 12 }}
                  formatter={(v, _k, ctx) => {
                    const row = ctx?.payload || {};
                    if (metric === "count") return [`${v} NCRs · ${fmtH(row.hours)}`, row.item || ""];
                    return [`${fmtH(v)} · ${row.count} NCRs`, row.item || ""];
                  }} />
                <Bar dataKey={metric} barSize={18} radius={[0, 2, 2, 0]}
                  onClick={(d) => d && setDrill({ problem: d.label, item: d.item })}
                  cursor="pointer">
                  {problems.map((row, i) => (
                    <Cell key={`${row.label}-${i}`} fill={colorForItem(row.item)} />
                  ))}
                  <LabelList dataKey={metric} position="right"
                    formatter={(v) => (metric === "count" ? v : fmtH(v))}
                    style={{ fill: "#E2E8F0", fontSize: 11, fontFamily: "monospace" }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Per-dept mini pareto grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="gm-per-dept-grid">
        {(data.per_dept || []).map((dp) => (
          <div key={dp.dept} className="bg-[#12141D] border border-border p-4"
               data-testid={`gm-dept-card-${dp.dept.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-[0.3em] uppercase font-mono" style={{ color: DEPT_COLORS[dp.dept] }}>{dp.dept}</div>
              <div className="text-[10px] font-mono text-muted-foreground">{fmtH(dp.total_hours)}</div>
            </div>
            <div className="font-heading font-bold text-2xl tabular-nums mb-3">{dp.count}</div>
            <ul className="space-y-1">
              {dp.top_problems.length === 0 && (
                <li className="text-[10px] text-muted-foreground italic">No issues</li>
              )}
              {dp.top_problems.map((p, i) => (
                <li key={i} className="flex items-center justify-between text-[11px]">
                  <span className="truncate text-white/80" title={p.label}>{p.label}</span>
                  <span className="font-mono text-muted-foreground ml-2">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Drill drawer */}
      {drill && (
        <div className="fixed inset-0 z-50" data-testid="gm-drill-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDrill(null)} />
          <aside className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-[#12141D] border-l border-border overflow-y-auto animate-in slide-in-from-right">
            <div className="p-5 sticky top-0 bg-[#12141D] border-b border-border flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{drill.item}</div>
                <h4 className="font-heading font-bold text-lg mt-1">{drill.problem}</h4>
              </div>
              <button data-testid="gm-drill-close" onClick={() => setDrill(null)} className="p-1 hover:text-white text-muted-foreground">
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              {drillLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
              {!drillLoading && drillRows && (
                <>
                  <div className="text-sm text-muted-foreground mb-4">
                    {drillRows.count} NCR{drillRows.count === 1 ? "" : "s"} · {fmtH(drillRows.total_hours)}
                  </div>
                  <div className="space-y-2">
                    {drillRows.rows.map((r, i) => (
                      <div key={i} className="border border-border p-3 bg-[#0A0B10]" data-testid={`gm-drill-row-${i}`}>
                        <div className="flex items-center justify-between text-[11px] mb-1">
                          <span className="font-mono text-white">{r.piece_mark || r.piece_sn || "—"}</span>
                          <span className="text-muted-foreground">{r.date || "—"}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{r._dept} · {r.plant}</div>
                        <div className="text-[11px] text-white/80 mt-1">{r.job}</div>
                        <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] font-mono">
                          <span className="px-1.5 py-0.5 border border-border text-muted-foreground">Status: {r.status || "—"}</span>
                          <span className="px-1.5 py-0.5 border border-border text-muted-foreground">Cause: {r.cause || "—"}</span>
                          <span className="px-1.5 py-0.5 border border-border text-[#FBBF24]">By: {r.created_by || "—"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function KpiCard({ testid, icon, label, value, accent }) {
  return (
    <div className="bg-[#12141D] border border-border p-4 relative overflow-hidden" data-testid={testid}>
      <span className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: accent }} />
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <span style={{ color: accent }}>{icon}</span> {label}
      </div>
      <div className="font-heading font-bold text-3xl tabular-nums mt-2">{value}</div>
    </div>
  );
}

function FilterSelect({ testid, label, value, onChange, options, allLabel }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0A0B10] border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-white cursor-pointer focus:outline-none focus:border-[#FBBF24] transition-colors max-w-[180px] truncate"
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}
